/**
 * Collegamenti nel corpo dei messaggi della webmail (decisione di Davide del 2/11/2026, dopo la truffa
 * via email che a fine ottobre ha colpito due caselle CIDIS).
 *
 * Il corpo HTML sta in un iframe con sandbox vuoto e i suoi collegamenti non si aprono; nel testo
 * semplice gli indirizzi restano testo. Si aprono solo dal riquadro «Collegamenti nel messaggio» sotto
 * il corpo (pagine/posta/CollegamentiMessaggio.tsx), dopo una finestra che mostra l'indirizzo intero e
 * il dominio. Qui le regole, senza React e senza dipendenze, provate in
 * apps/api/test/posta-collegamenti-sicuri.test.ts: l'estrazione, il dominio registrabile e i controlli
 * che fanno comparire gli avvisi.
 *
 * Non c'entrano i collegamenti agli allegati grandi (509), che il gestionale crea e revoca.
 */

/** Un collegamento del messaggio: l'indirizzo come lo apre il browser e i testi visibili che lo portano. */
export type CollegamentoTrovato = {
  /** URL.href: schema e dominio in minuscolo, dominio in punycode, spazi codificati. */
  url: string
  /** Testi visibili dei collegamenti verso lo stesso indirizzo, senza ripetizioni; vuoto dal testo semplice. */
  testi: string[]
}

export type ElencoCollegamenti = { collegamenti: CollegamentoTrovato[]; altri: number }

/** Oltre questo numero il riquadro dice «e altri N»: una newsletter ne porta anche un centinaio. */
export const MASSIMO_COLLEGAMENTI = 20
const MASSIMO_TESTI = 5
const LUNGHEZZA_TESTO = 200

const spazi = (s: string) => s.replace(/\s+/g, ' ').trim()
const taglia = (s: string, n: number) => (s.length > n ? `${Array.from(s).slice(0, n).join('')}…` : s)

/** L'indirizzo letto con il parser del browser, se e' http o https; altrimenti null. */
export function indirizzoWeb(valore: string): URL | null {
  let u: URL
  try { u = new URL(valore.trim()) } catch { return null }
  return u.protocol === 'http:' || u.protocol === 'https:' ? u : null
}

/**
 * Elenco senza ripetizioni, nell'ordine del messaggio. Lo stesso indirizzo con testi diversi resta una
 * voce sola che li tiene tutti: il controllo sul testo ingannevole li guarda uno per uno.
 */
export function collegamentiDaAncore(ancore: { href: string | null; testo: string }[],
  massimo = MASSIMO_COLLEGAMENTI): ElencoCollegamenti {
  const perUrl = new Map<string, CollegamentoTrovato>()
  for (const a of ancore) {
    const u = a.href ? indirizzoWeb(a.href) : null
    if (!u) continue
    let voce = perUrl.get(u.href)
    if (!voce) { voce = { url: u.href, testi: [] }; perUrl.set(u.href, voce) }
    const testo = taglia(spazi(a.testo), LUNGHEZZA_TESTO)
    if (testo && !voce.testi.includes(testo) && voce.testi.length < MASSIMO_TESTI) voce.testi.push(testo)
  }
  const tutti = [...perUrl.values()]
  return { collegamenti: tutti.slice(0, massimo), altri: Math.max(0, tutti.length - massimo) }
}

/**
 * Dall'HTML gia' ripulito (posta.ripulisciHtml): gli <a> con href http o https e il loro testo. Un
 * collegamento fatto solo di un'immagine prende il testo alternativo, che spesso dice dove porta.
 * DOMParser crea un documento inerte: niente script, niente caricamenti.
 */
export function collegamentiDaHtml(html: string, massimo = MASSIMO_COLLEGAMENTI): ElencoCollegamenti {
  const documento = new DOMParser().parseFromString(html, 'text/html')
  const ancore = Array.from(documento.querySelectorAll('a[href]'), (a) => {
    const testo = spazi(a.textContent ?? '')
    const alt = testo ? '' : spazi(a.querySelector('img[alt]')?.getAttribute('alt') ?? '')
    return { href: a.getAttribute('href'), testo: testo || (alt ? `(immagine: ${alt})` : '') }
  })
  return collegamentiDaAncore(ancore, massimo)
}

/** Indirizzi con lo schema, fino al primo spazio, virgolette o parentesi angolari. */
const URL_NEL_TESTO = /\bhttps?:\/\/[^\s<>"'«»“”‘’]+/gi
const PARENTESI: Record<string, string> = { ')': '(', ']': '[', '}': '{' }
const conta = (s: string, c: string) => s.split(c).length - 1

/** Toglie la punteggiatura che chiude la frase e le parentesi chiuse senza la loro aperta. */
function senzaCoda(indirizzo: string): string {
  let s = indirizzo
  for (;;) {
    const ultimo = s.at(-1) ?? ''
    if (ultimo && '.,;:!?…'.includes(ultimo)) { s = s.slice(0, -1); continue }
    const aperta = PARENTESI[ultimo]
    if (aperta && conta(s, ultimo) > conta(s, aperta)) { s = s.slice(0, -1); continue }
    return s
  }
}

/**
 * Il testo semplice diviso in pezzi: testo e indirizzi http e https, per mostrarli nel messaggio come
 * collegamenti che aprono la finestra di conferma (2/11/2026). La punteggiatura finale resta testo.
 */
export function pezziDelTesto(testo: string): ({ testo: string } | { indirizzo: string })[] {
  const pezzi: ({ testo: string } | { indirizzo: string })[] = []
  let ultimo = 0
  for (const m of testo.matchAll(URL_NEL_TESTO)) {
    const indirizzo = senzaCoda(m[0])
    const inizio = m.index ?? 0
    if (inizio > ultimo) pezzi.push({ testo: testo.slice(ultimo, inizio) })
    pezzi.push({ indirizzo })
    ultimo = inizio + indirizzo.length
  }
  if (ultimo < testo.length) pezzi.push({ testo: testo.slice(ultimo) })
  return pezzi
}

/** Dal testo semplice: gli indirizzi http e https. Senza testo visibile: il testo e' l'indirizzo. */
export function collegamentiDaTesto(testo: string, massimo = MASSIMO_COLLEGAMENTI): ElencoCollegamenti {
  const ancore = Array.from(testo.matchAll(URL_NEL_TESTO), (m) => ({ href: senzaCoda(m[0]), testo: '' }))
  return collegamentiDaAncore(ancore, massimo)
}

// --- dominio registrabile ---------------------------------------------------------------------------

/**
 * Suffissi a due livelli piu' comuni, sotto i quali il nome registrato ha tre livelli (bbc.co.uk).
 * Non e' la Public Suffix List: un elenco breve, senza librerie. Per gli altri domini il nome
 * registrato sono gli ultimi due livelli. Le piattaforme che danno sottodomini a chiunque (web.app,
 * sharepoint.com, github.io) restano fuori di proposito: il dominio mostrato e' la piattaforma, ed e'
 * l'informazione giusta per chi legge.
 */
const SUFFISSI_DOPPI = new Set([
  'gov.it', 'edu.it',
  'co.uk', 'org.uk', 'me.uk', 'ltd.uk', 'plc.uk', 'net.uk', 'ac.uk', 'gov.uk', 'sch.uk', 'nhs.uk', 'police.uk',
  'com.au', 'net.au', 'org.au', 'edu.au', 'gov.au', 'asn.au', 'id.au',
  'co.nz', 'org.nz', 'net.nz', 'govt.nz', 'ac.nz',
  'co.jp', 'ne.jp', 'or.jp', 'ac.jp', 'go.jp', 'co.kr', 'or.kr',
  'com.cn', 'net.cn', 'org.cn', 'gov.cn', 'edu.cn', 'com.hk', 'org.hk', 'com.tw', 'com.sg', 'com.my',
  'com.ph', 'com.vn', 'co.th', 'co.id', 'or.id', 'go.id',
  'co.in', 'net.in', 'org.in', 'gov.in', 'ac.in', 'com.pk', 'com.bd',
  'com.br', 'net.br', 'org.br', 'gov.br', 'edu.br', 'com.ar', 'com.mx', 'gob.mx', 'com.co', 'com.pe',
  'com.tr', 'gov.tr', 'co.il', 'org.il', 'ac.il', 'gov.il', 'com.sa', 'com.eg',
  'co.za', 'org.za', 'gov.za', 'com.ng', 'co.ma', 'com.tn',
  'com.ua', 'com.pl', 'com.gr', 'gov.gr', 'com.es', 'org.es', 'gob.es', 'com.al', 'org.al', 'gov.al',
])

const IPV4 = /^\d{1,3}(?:\.\d{1,3}){3}$/

/** Vero per un indirizzo IP al posto del nome; URL.hostname scrive gia' in decimale le forme strane. */
export const eIndirizzoIp = (host: string) => IPV4.test(host) || host.startsWith('[')

const senzaPuntoFinale = (host: string) => host.toLowerCase().replace(/\.$/, '')

/**
 * Il nome registrato, cioe' la parte che conta: «cidisonlus.org.esempio.com» e' esempio.com. Ultimi due
 * livelli, tre sotto i suffissi di SUFFISSI_DOPPI; per un IP, l'IP.
 */
export function dominioRegistrabile(host: string): string {
  const h = senzaPuntoFinale(host)
  if (eIndirizzoIp(h)) return h
  const parti = h.split('.')
  if (parti.length <= 2) return h
  return parti.slice(SUFFISSI_DOPPI.has(parti.slice(-2).join('.')) ? -3 : -2).join('.')
}

// --- il testo visibile mostra un indirizzo? ----------------------------------------------------------

const PUNTEGGIATURA_INTORNO = /^[([{<«"'“‘]+|[)\]}>»"'”’.,;:!?…]+$/g
const NOME_A_DOMINIO = /^(?:[a-z0-9¡-￿](?:[a-z0-9¡-￿-]*[a-z0-9¡-￿])?\.)+[a-z¡-￿]{2,}$/i

/** «modulo.pdf» e' un file, non un sito, anche se .zip e .mov esistono come domini. */
const ESTENSIONI_FILE = new Set([
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp', 'rtf', 'txt', 'csv', 'xml', 'json',
  'jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'svg', 'zip', 'rar', '7z', 'mp3', 'mp4', 'mov', 'avi', 'wav',
  'eml', 'msg', 'p7m', 'htm', 'html', 'php', 'asp', 'aspx', 'exe',
])

/** Dentro una frase un nome con il punto conta come dominio solo con uno di questi suffissi. */
const SUFFISSI_FRASE = new Set([
  'it', 'com', 'org', 'net', 'eu', 'info', 'gov', 'edu', 'biz', 'io', 'uk', 'de', 'fr', 'es', 'ch',
  'app', 'online', 'site', 'xyz', 'top', 'shop',
])

function nomeDellaParola(parola: string, sola: boolean): string | null {
  const conSchema = /^https?:\/\//i.test(parola)
  if (!conSchema && parola.includes('@')) return null // un indirizzo email, non un sito
  const u = indirizzoWeb(conSchema ? parola : `http://${parola}`)
  if (!u) return null
  if (conSchema) return senzaPuntoFinale(u.hostname)
  const grezzo = parola.split(/[/?#:]/)[0] ?? ''
  if (IPV4.test(grezzo)) return sola ? u.hostname : null
  if (!NOME_A_DOMINIO.test(grezzo)) return null
  const ultimo = grezzo.split('.').pop()!.toLowerCase()
  if (ESTENSIONI_FILE.has(ultimo)) return null
  return sola || /^www\./i.test(grezzo) || SUFFISSI_FRASE.has(ultimo) ? senzaPuntoFinale(u.hostname) : null
}

/**
 * I nomi di sito che il testo visibile mostra. Se il testo e' una parola sola basta che somigli a un
 * indirizzo («www.bancaintesa.it», «bancaintesa.it/accedi»); dentro una frase contano le parole con
 * http:// o https://, con www. o con un suffisso comune, cosi' «scarica modulo.pdf» non fa scattare nulla.
 */
export function dominiNelTesto(testo: string): string[] {
  const parole = testo.split(/\s+/).map((p) => p.replace(PUNTEGGIATURA_INTORNO, '')).filter(Boolean)
  const nomi = new Set<string>()
  for (const parola of parole) {
    const nome = nomeDellaParola(parola, parole.length === 1)
    if (nome) nomi.add(nome)
  }
  return [...nomi]
}

// --- reindirizzamenti e accorciatori ---------------------------------------------------------------

/** Bing scrive la destinazione in base64url preceduta da «a1». */
function daBing(valore: string): string {
  if (!valore.startsWith('a1')) return valore
  const b = valore.slice(2).replace(/-/g, '+').replace(/_/g, '/')
  try { return atob(b + '='.repeat((4 - (b.length % 4)) % 4)) } catch { return '' }
}

type Reindirizzamento = {
  servizio: string
  vale: (u: URL, dominio: string) => boolean
  parametri: string[]
  decodifica?: (valore: string) => string
}

/**
 * Servizi noti che rimandano alla destinazione scritta in un parametro: ci si puo' fidare che portino
 * li', e la destinazione si mostra. Un parametro con un indirizzo dentro un sito qualsiasi invece non
 * dice dove si arriva davvero (vedi il controllo «parametro»).
 */
const REINDIRIZZAMENTI: Reindirizzamento[] = [
  { servizio: 'Google', vale: (u, d) => /^google\.[a-z.]+$/.test(d) && u.pathname === '/url', parametri: ['q', 'url'] },
  { servizio: 'Bing', vale: (u, d) => d === 'bing.com' && u.pathname.startsWith('/ck/'), parametri: ['u'], decodifica: daBing },
  {
    servizio: 'Outlook (Safe Links)', vale: (u) => u.hostname.endsWith('.safelinks.protection.outlook.com'),
    parametri: ['url'],
  },
  { servizio: 'Facebook', vale: (u, d) => d === 'facebook.com' && u.pathname === '/l.php', parametri: ['u'] },
  { servizio: 'Instagram', vale: (u) => u.hostname === 'l.instagram.com', parametri: ['u'] },
  { servizio: 'YouTube', vale: (u, d) => d === 'youtube.com' && u.pathname === '/redirect', parametri: ['q'] },
  { servizio: 'LinkedIn', vale: (u, d) => d === 'linkedin.com' && u.pathname.startsWith('/redir/'), parametri: ['url'] },
  { servizio: 'Slack', vale: (_u, d) => d === 'slack-redir.net', parametri: ['url'] },
  { servizio: 'DuckDuckGo', vale: (u, d) => d === 'duckduckgo.com' && u.pathname.startsWith('/l/'), parametri: ['uddg'] },
]

/** Accorciatori di indirizzi: la destinazione vera si scopre solo aprendo il collegamento. */
const ACCORCIATORI = new Set([
  'bit.ly', 't.co', 'tinyurl.com', 'goo.gl', 'ow.ly', 'is.gd', 'buff.ly', 'rebrand.ly', 'cutt.ly',
  'shorturl.at', 'tiny.cc', 'rb.gy', 'lnkd.in', 't.ly',
])

/** Il passo successivo se l'indirizzo e' di un servizio di reindirizzamento noto. */
function passoNoto(u: URL): { servizio: string; url: URL } | null {
  const dominio = dominioRegistrabile(u.hostname)
  for (const r of REINDIRIZZAMENTI) {
    if (!r.vale(u, dominio)) continue
    for (const p of r.parametri) {
      const valore = u.searchParams.get(p)
      const dopo = valore ? indirizzoWeb(r.decodifica ? r.decodifica(valore) : valore) : null
      if (dopo) return { servizio: r.servizio, url: dopo }
    }
  }
  return null
}

// --- controlli ---------------------------------------------------------------------------------------

export type Controllo =
  /** Il testo visibile mostra un sito e il collegamento porta a un altro: l'avviso rosso. */
  | { tipo: 'testo-diverso'; testo: string; dominioTesto: string; dominio: string }
  /** Nome utente prima del dominio (https://www.banca.it@esempio.com): porta a esempio.com. */
  | { tipo: 'credenziali'; prima: string; host: string; dominio: string }
  /** Il nome comincia con quello di un altro sito: «cidisonlus.org.esempio.com» e' esempio.com. */
  | { tipo: 'sottodominio'; prima: string; dominio: string }
  | { tipo: 'ip'; host: string }
  /** Dominio con parti xn-- o con caratteri non ASCII: lettere che imitano quelle di un nome noto. */
  | { tipo: 'punycode'; host: string }
  | { tipo: 'accorciato'; servizio: string }
  | { tipo: 'reindirizzamento'; servizio: string; destinazione: string; dominio: string }
  /** Un altro indirizzo in un parametro: il sito puo' rimandare li', ma non e' detto. */
  | { tipo: 'parametro'; parametro: string; destinazione: string; dominio: string }
  | { tipo: 'http'; host: string }

export type Verifica = {
  url: string
  /** Il primo testo visibile; vuoto per gli indirizzi del testo semplice. */
  testo: string
  /** Nome del sito senza punto finale, in punycode se ha lettere non ASCII. */
  host: string
  /** La parte del nome prima del dominio registrabile, con il punto: «cidisonlus.org.» */
  sottodominio: string
  dominio: string
  /** Dove si arriva se il collegamento passa da reindirizzamenti noti; altrimenti null. */
  destinazione: string | null
  controlli: Controllo[]
  /** C'e' l'avviso rosso. */
  grave: boolean
}

const ORDINE: Controllo['tipo'][] = [
  'testo-diverso', 'credenziali', 'sottodominio', 'ip', 'punycode', 'accorciato', 'reindirizzamento', 'parametro',
  'http',
]

/**
 * La parte prima del dominio registrabile, se a sua volta somiglia a un sito: un'etichetta dopo la prima
 * e' un suffisso comune («cidisonlus.org.», «login.paypal.com.»). «it.wikipedia.org» non conta.
 */
export function sottodominioIngannevole(host: string): string | null {
  const h = senzaPuntoFinale(host)
  const dominio = dominioRegistrabile(h)
  if (dominio === h || !h.endsWith(`.${dominio}`)) return null
  const prima = h.slice(0, -dominio.length - 1)
  return prima.split('.').slice(1).some((e) => SUFFISSI_FRASE.has(e)) ? prima : null
}

const nonAscii = (s: string) => /[^\x00-\x7f]/.test(s)

/** Nome utente e password restano codificati in URL: si decodificano per mostrarli, se si puo'. */
function decodificato(s: string): string {
  try { return decodeURIComponent(s) } catch { return s }
}

/** I controlli sul collegamento e, se passa da reindirizzamenti noti, su ogni passo fino alla destinazione. */
export function verificaCollegamento(c: CollegamentoTrovato): Verifica {
  const u = new URL(c.url)
  const host = senzaPuntoFinale(u.hostname)
  const dominio = dominioRegistrabile(host)

  const passi: { servizio: string; url: URL }[] = []
  let finale = u
  for (let i = 0; i < 3; i++) {
    const passo = passoNoto(finale)
    if (!passo) break
    passi.push(passo)
    finale = passo.url
  }
  const dominioFinale = dominioRegistrabile(finale.hostname)

  const controlli: Controllo[] = []
  const visti = new Set<string>()
  const aggiungi = (k: Controllo, chiave: string) => {
    if (visti.has(chiave)) return
    visti.add(chiave)
    controlli.push(k)
  }

  // Si confronta con la destinazione finale: «https://esempio.com» che passa da Google e arriva a
  // esempio.com non inganna nessuno; «google.com» che passa da Google e arriva altrove si'.
  for (const testo of c.testi) {
    const diverso = dominiNelTesto(testo).map(dominioRegistrabile).find((d) => d !== dominioFinale)
    if (diverso) {
      aggiungi({ tipo: 'testo-diverso', testo, dominioTesto: diverso, dominio: dominioFinale }, 'testo')
      break
    }
  }

  for (const x of [u, ...passi.map((p) => p.url)]) {
    const h = senzaPuntoFinale(x.hostname)
    if (x.username || x.password) {
      const prima = decodificato(x.username) + (x.password ? `:${decodificato(x.password)}` : '')
      aggiungi({ tipo: 'credenziali', prima: taglia(prima, 60), host: h, dominio: dominioRegistrabile(h) }, `c:${h}`)
    }
    const finto = sottodominioIngannevole(h)
    if (finto) aggiungi({ tipo: 'sottodominio', prima: finto, dominio: dominioRegistrabile(h) }, `s:${h}`)
    if (eIndirizzoIp(h)) aggiungi({ tipo: 'ip', host: h }, `i:${h}`)
    if (nonAscii(h) || h.split('.').some((parte) => parte.startsWith('xn--'))) aggiungi({ tipo: 'punycode', host: h }, `p:${h}`)
    const d = dominioRegistrabile(h)
    if (ACCORCIATORI.has(d)) aggiungi({ tipo: 'accorciato', servizio: d }, 'accorciato')
    if (x.protocol === 'http:') aggiungi({ tipo: 'http', host: h }, `h:${h}`)
  }

  if (passi.length) {
    aggiungi({
      tipo: 'reindirizzamento', servizio: passi.map((p) => p.servizio).join(', poi '),
      destinazione: finale.href, dominio: dominioFinale,
    }, 'reindirizzamento')
  }

  for (const [parametro, valore] of finale.searchParams) {
    const dentro = indirizzoWeb(valore)
    if (dentro && dominioRegistrabile(dentro.hostname) !== dominioFinale) {
      aggiungi({
        tipo: 'parametro', parametro, destinazione: dentro.href, dominio: dominioRegistrabile(dentro.hostname),
      }, 'parametro')
      break
    }
  }

  controlli.sort((a, b) => ORDINE.indexOf(a.tipo) - ORDINE.indexOf(b.tipo))
  return {
    url: c.url,
    testo: c.testi[0] ?? '',
    host,
    sottodominio: host.endsWith(dominio) ? host.slice(0, host.length - dominio.length) : '',
    dominio,
    destinazione: passi.length ? finale.href : null,
    controlli,
    grave: controlli.some((k) => k.tipo === 'testo-diverso'),
  }
}
